import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Container,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  Button,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import { CircuitTabs, updateMenuItems } from "../CircuitOrganize";

import {
  getGameSystems,
  handlePagingAll,
} from "../../../components/api/APIUtilities";
import {
  areObjectsEqual,
  findGetParameter,
  getData,
  postData,
} from "../../../components/utilities/UtilityFunctions";

import * as Yup from "yup";
import { FieldArray, Formik } from "formik";

import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";

import DatePickerComponent from "../../../components/utilities/DatePickerComponent";
import moment from "moment";

import ImageUploader, {
  uploadBas64ToS3,
} from "../../../components/utilities/ImageUploader";
import GenericPicker from "../../../components/utilities/GenericPicker";
import HelpButton from "../../../components/utilities/HelpButton";
import {
  BCPAngleDown,
  BCPSquarePlus,
  BCPTrash,
} from "../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import { useConfirm } from "material-ui-confirm";

const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");
const APP_NAME = getEnvironmentVariable("APP_NAME") || "BCP";
const LABEL_GAMESYSTEM =
  getEnvironmentVariable("LABEL_GAMESYSTEM") || "Game System";
const SUPPORTS_LOYALTY_STORE =
  getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") === "true";
const SUPPORTS_HOBBY_TRACK =
  getEnvironmentVariable("SUPPORTS_HOBBY_TRACK") === "true";

export default function CircuitDetails(props) {
  const { t } = useTranslation();
  const { activeTab, handleTabChange, circuit, authContext, setItemsMiddle } =
    props;

  const navigate = useNavigate();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const organizationId = findGetParameter("organizationId");

  const [gameStore, setGameStore] = useState(null);
  const [eloClasses, setEloClasses] = useState(null);

  const [gameSystems, setGameSystems] = useState([]);
  const [gameSystemDict, setGameSystemDict] = useState({});

  const initialRankingsAlgorithms =
    COMPANY_FLAG === "bcp" && authContext?.authState?.identity?.isAdmin === true
      ? ["BCP Points", "ITC Points", "Texas Masters Points"]
      : [`${APP_NAME} Points`];
  const [rankingsAlgorithms, setRankingsAlgorithms] = useState(
    initialRankingsAlgorithms
  );

  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    const check = async () => {
      try {
        if (!circuit?.id) {
          var apiStore = await getData(`/gamestores/${organizationId}`, {
            queryStringParameters: {},
          });

          var storeUser = await handlePagingAll(`/gamestoreusers`, {
            limit: 100,
            gameStoreId: organizationId,
            userId: authContext?.authState?.identity?.userId,
          });
          apiStore.myStoreUser = storeUser?.length > 0 ? storeUser[0] : null;

          if (
            authContext?.authState?.identity?.isAdmin !== true &&
            authContext?.authState?.identity?.userId !== apiStore.ownerId &&
            !apiStore.myStoreUser?.createLeagues
          ) {
            postNotification(
              "error",
              "You do not have access to create Circuits for this Organization"
            );
            navigate("/organize/organizations");
            return;
          }

          if (apiStore?.itcPointsAllowed) {
            setRankingsAlgorithms([...initialRankingsAlgorithms, "ITC Points"]);
          }

          updateMenuItems(setItemsMiddle, apiStore);
          setGameStore(apiStore);

          var apiGameSystems = await getGameSystems();
          setGameSystems(apiGameSystems);
          var apiGameSystemDict = {};
          apiGameSystems.forEach((apiGameSystem) => {
            apiGameSystemDict[apiGameSystem.code] = apiGameSystem;
          });
          setGameSystemDict(apiGameSystemDict);
        } else if (circuit?.gameStore) {
          setGameStore(circuit.gameStore);

          if (COMPANY_FLAG === "bcp") {
            // add algorithms
            if (!rankingsAlgorithms.includes("ITC Points")) {
              if (circuit.itc || circuit.gameStore?.itcPointsAllowed) {
                setRankingsAlgorithms([
                  ...initialRankingsAlgorithms,
                  "ITC Points",
                ]);
              }
            }
            if (!rankingsAlgorithms.includes("Texas Points")) {
              if (circuit.texasMasters) {
                setRankingsAlgorithms([
                  ...initialRankingsAlgorithms,
                  "ITC Points",
                ]);
              }
            }
          }
        }

        if (circuit.ELOOptions) {
          setEloClasses(circuit.ELOOptions?.ELOClasses || []);
        } else {
          setEloClasses([
            {
              label: "Class J",
              min: 100,
              max: 199,
            },
            { label: "Class I", min: 200, max: 399 },
            { label: "Class H", min: 400, max: 599 },
            { label: "Class G", min: 600, max: 799 },
            { label: "Class F", min: 800, max: 999 },
            { label: "Class E", min: 1000, max: 1199 },
            { label: "Class D", min: 1200, max: 1399 },
            { label: "Class C", min: 1400, max: 1599 },
            { label: "Class B", min: 1600, max: 1799 },
            { label: "Class A", min: 1800, max: 1999 },
            { label: "Candidate Master", min: 2000, max: 2199 },
            { label: "National Master", min: 2200, max: 2399 },
            { label: "Senior Master", min: 2400 },
          ]);
        }
      } catch (err) {
        postNotification("error", err);
      }

      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [circuit]);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondaryecondary",
          }}
        >
          <Typography variant="h3">{circuit?.name || "New Circuit"}</Typography>

          <CircuitTabs
            circuit={circuit}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            p: 2,
          }}
        >
          {eloClasses && (
            <Formik
              initialValues={{
                name: circuit?.name || "",
                startDate: circuit?.startDate
                  ? moment(circuit.startDate).format("YYYY-MM-DD")
                  : moment().startOf("month").format("YYYY-MM-DD"),
                endDate: circuit?.endDate
                  ? moment(circuit.endDate).format("YYYY-MM-DD")
                  : moment()
                      .add(11, "months")
                      .endOf("month")
                      .format("YYYY-MM-DD"),
                public: circuit?.public !== undefined ? circuit.public : false,
                bcpDefault:
                  circuit?.bcpDefault !== undefined
                    ? circuit.bcpDefault
                    : false,
                active: circuit?.active !== undefined ? circuit.active : true,
                closed: circuit?.closed !== undefined ? circuit.closed : false,
                hidePlacings:
                  circuit?.hidePlacings !== undefined
                    ? circuit.hidePlacings
                    : false,

                circuitLeague:
                  circuit?.circuitLeague !== undefined
                    ? circuit.circuitLeague
                    : gameStore?.circuitAccess
                    ? true
                    : false,
                storeLeague:
                  circuit?.storeLeague !== undefined
                    ? circuit.storeLeague
                    : !gameStore?.circuitAccess,
                defaultRankings:
                  circuit?.defaultRankings !== undefined
                    ? circuit.defaultRankings
                    : false,

                hobby: circuit?.hobby !== undefined ? circuit.hobby : false,

                rankingsAlgorithm: circuit?.itc
                  ? "ITC Points"
                  : circuit?.texasMasters
                  ? "Texas Masters Points"
                  : circuit?.solforgeLeague ||
                    (!circuit?.id && COMPANY_FLAG === "solforge")
                  ? "Solforge Points"
                  : `${APP_NAME} Points`,

                tokenTypes: circuit?.tokenTypes || [],

                loyaltyLeague:
                  circuit?.loyaltyLeague !== undefined
                    ? circuit.loyaltyLeague
                    : false,

                numPlayerPlacings:
                  circuit === undefined ? 4 : circuit?.numPlayerPlacings || "",

                numTeamPlacings:
                  circuit === undefined ? 10 : circuit?.numTeamPlacings || "",
                maxTeamPlayerPlacings:
                  circuit === undefined
                    ? 3
                    : circuit?.maxTeamPlayerPlacings || "",

                numFactionPlacings:
                  circuit === undefined ? 4 : circuit?.numFactionPlacings || "",

                maxRTT: circuit?.maxRTT !== undefined ? circuit.maxRTT : "",
                maxGT: circuit?.maxGT !== undefined ? circuit.maxGT : "",

                GTEventSize:
                  circuit === undefined ? 28 : circuit?.GTEventSize || "",
                MajorEventSize:
                  circuit === undefined ? 58 : circuit?.MajorEventSize || "",
                SuperMajorEventSize:
                  circuit === undefined
                    ? 205
                    : circuit?.SuperMajorEventSize || "",

                ELOOptions: circuit.ELOOptions
                  ? {
                      ...circuit.ELOOptions,
                      ELOClasses: eloClasses,
                    }
                  : {
                      defaultELO: 1000,
                      minimumELO: 100,
                      maxRatingDifference: 400,
                      k: {
                        numerator: 800,
                        minGamesPlayed: 3,
                      },
                      ne: {
                        maxN: 50,
                        level1: 2355,
                        level2: 2569,
                        a: 0.662,
                        b: 0.00000739,
                      },
                      t: 12,
                      b: {
                        minGamesPlayed: 3,
                      },
                      floorELOClass: false,
                      ELOClasses: eloClasses,
                    },
                defaultPlacingType:
                  circuit.defaultPlacingType || "Ranking Points",
              }}
              validationSchema={Yup.object().shape({
                name: Yup.string()
                  .min(3, "Minimum 3 Characters!")
                  .max(255, "Maximum 255 Characters!")
                  .required("Required"),
              })}
              onSubmit={async (values) => {
                let rebuildLeague = false;
                // Check if we need to rebuild the circuit
                if (circuit?.id) {
                  let changed = false;
                  const circuitRankingsAlgo = circuit?.itc
                    ? "ITC Points"
                    : circuit?.texasMasters
                    ? "Texas Masters Points"
                    : circuit?.jascoLeague
                    ? "Jasco Points"
                    : circuit?.solforgeLeague
                    ? "Solforge Points"
                    : `${APP_NAME} Points`;

                  if (
                    values.numPlayerPlacings &&
                    values.numPlayerPlacings !== circuit.numPlayerPlacings
                  )
                    changed = true;

                  if (
                    values.numTeamPlacings &&
                    values.numTeamPlacings !== circuit.numTeamPlacings
                  )
                    changed = true;

                  if (
                    values.maxTeamPlayerPlacings &&
                    values.maxTeamPlayerPlacings !==
                      circuit.maxTeamPlayerPlacings
                  )
                    changed = true;

                  if (
                    values.numFactionPlacings &&
                    values.numFactionPlacings !== circuit.numFactionPlacings
                  )
                    changed = true;

                  if (
                    values.rankingsAlgorithm &&
                    values.rankingsAlgorithm !== circuitRankingsAlgo
                  )
                    changed = true;

                  // Checks to see if circuit.itc is true. If it is, check to see if any of the ITC fields have been changed
                  if (values.rankingsAlgorithm === "ITC Points") {
                    if (!circuit.itc) {
                      changed = true;
                    } else {
                      if (values.maxRTT && values.maxRTT !== circuit.maxRTT)
                        changed = true;

                      if (values.maxGT && values.maxGT !== circuit.maxGT)
                        changed = true;

                      if (
                        values.GTEventSize &&
                        values.GTEventSize !== circuit.GTEventSize
                      )
                        changed = true;

                      if (
                        values.MajorEventSize &&
                        values.MajorEventSize !== circuit.MajorEventSize
                      )
                        changed = true;

                      if (
                        values.SuperMajorEventSize &&
                        values.SuperMajorEventSize !==
                          circuit.SuperMajorEventSize
                      )
                        changed = true;
                    }
                  }

                  if (!areObjectsEqual(values.ELOOptions, circuit.ELOOptions))
                    changed = true;

                  if (changed) {
                    try {
                      await confirm({
                        title: `Are you sure?`,
                        description:
                          "You have made changes that require a recalculation of your Circuit Points. This may take a few minutes to update. Are you sure you want to continue?",
                        dialogProps: {
                          sx: {
                            zIndex: 99999,
                          },
                        },
                      });

                      rebuildLeague = true;
                    } catch (err) {
                      return;
                    }
                  }
                }

                try {
                  setProcessing(true);

                  var sanitizedValues = {
                    ...values,
                  };

                  if (authContext?.authState?.identity?.isAdmin !== true) {
                    delete sanitizedValues.public;
                  }

                  // handle ELOClasses
                  if (sanitizedValues.ELOOptions?.ELOClasses.length) {
                    var prevMax = -1;
                    sanitizedValues.ELOOptions.ELOClasses.forEach(
                      (eloClass) => {
                        if (prevMax !== -1 && eloClass.min !== prevMax + 1) {
                          throw new Error(
                            `ELO Class: ${eloClass.label} should have a minimum of ${prevMax}.`
                          );
                        }
                        if (
                          eloClass.max &&
                          eloClass.max !== "" &&
                          eloClass.max < eloClass.min
                        ) {
                          throw new Error(
                            `ELO Class: ${eloClass.label} has a max value less than it's min value.`
                          );
                        }
                      }
                    );
                  }

                  sanitizedValues.texasMasters =
                    sanitizedValues.rankingsAlgorithm ===
                    "Texas Masters Points";
                  sanitizedValues.jascoLeague =
                    sanitizedValues.rankingsAlgorithm === "Jasco Points";
                  sanitizedValues.itc =
                    sanitizedValues.rankingsAlgorithm === "ITC Points";
                  sanitizedValues.solforgeLeague =
                    sanitizedValues.rankingsAlgorithm === "Solforge Points";

                  if (sanitizedValues.numPlayerPlacings === "") {
                    delete sanitizedValues.numPlayerPlacings;
                  }
                  if (sanitizedValues.numTeamPlacings === "") {
                    delete sanitizedValues.numTeamPlacings;
                  }
                  if (sanitizedValues.maxTeamPlayerPlacings === "") {
                    delete sanitizedValues.maxTeamPlayerPlacings;
                  }
                  if (sanitizedValues.numFactionPlacings === "") {
                    delete sanitizedValues.numFactionPlacings;
                  }

                  if (!sanitizedValues.itc) {
                    delete sanitizedValues.maxRTT;

                    delete sanitizedValues.maxGT;
                    delete sanitizedValues.GTEventSize;

                    delete sanitizedValues.MajorEventSize;
                    delete sanitizedValues.SuperMajorEventSize;
                  }
                  delete sanitizedValues.rankingsAlgorithm;

                  if (sanitizedValues.circuitImage) {
                    // upload image to s3
                    var newFile = await uploadBas64ToS3(
                      sanitizedValues.circuitImage
                    );
                    delete sanitizedValues.circuitImage;
                    if (newFile.fileId) {
                      sanitizedValues.photoFileId = newFile.fileId;
                    }
                  }

                  if (circuit?.id) {
                    var unset = {};
                    if (sanitizedValues.numPlayerPlacings === "") {
                      delete sanitizedValues.numPlayerPlacings;
                      unset.numPlayerPlacings = "";
                    }
                    if (sanitizedValues.numTeamPlacings === "") {
                      delete sanitizedValues.numTeamPlacings;
                      unset.numTeamPlacings = "";
                    }
                    if (sanitizedValues.maxTeamPlayerPlacings === "") {
                      delete sanitizedValues.maxTeamPlayerPlacings;
                      unset.maxTeamPlayerPlacings = "";
                    }
                    if (sanitizedValues.numFactionPlacings === "") {
                      delete sanitizedValues.numFactionPlacings;
                      unset.numFactionPlacings = "";
                    }

                    if (sanitizedValues.itc) {
                      if (sanitizedValues.maxRTT === "") {
                        delete sanitizedValues.maxRTT;
                        unset.maxRTT = "";
                      }
                      if (sanitizedValues.maxGT === "") {
                        delete sanitizedValues.maxGT;
                        unset.maxGT = "";
                      }
                      if (sanitizedValues.GTEventSize === "") {
                        delete sanitizedValues.GTEventSize;
                        unset.GTEventSize = "";
                      }
                      if (sanitizedValues.MajorEventSize === "") {
                        delete sanitizedValues.MajorEventSize;
                        unset.MajorEventSize = "";
                      }
                      if (sanitizedValues.SuperMajorEventSize === "") {
                        delete sanitizedValues.SuperMajorEventSize;
                        unset.SuperMajorEventSize = "";
                      }
                    }

                    var body = {
                      set: sanitizedValues,
                    };
                    if (Object.keys(unset).length > 0) {
                      body.unset = unset;
                    }
                    // update
                    await postData(`/leagues/${circuit.id}`, {
                      body: {
                        set: sanitizedValues,
                      },
                    });
                    postNotification(
                      "success",
                      t("notifications.success.circuitUpdated")
                    );

                    if (rebuildLeague) {
                      await postData(`/leagues/${circuit.id}/rebuildLeague`);
                      postNotification(
                        "info",
                        "Circuit Rebuild Scheduled, this may take a few minutes to complete"
                      );
                    }
                  } else {
                    sanitizedValues.ownerId =
                      authContext?.authState?.identity?.userId;
                    sanitizedValues.gameStoreId = organizationId;

                    if (sanitizedValues.gameType) {
                      sanitizedValues.gameSystemId =
                        gameSystemDict[sanitizedValues.gameType]?.id;
                      delete sanitizedValues.gameType;
                    } else {
                      throw new Error("invalid game system");
                    }

                    if (sanitizedValues.numPlayerPlacings === "") {
                      delete sanitizedValues.numPlayerPlacings;
                    }
                    if (sanitizedValues.numTeamPlacings === "") {
                      delete sanitizedValues.numTeamPlacings;
                    }
                    if (sanitizedValues.maxTeamPlayerPlacings === "") {
                      delete sanitizedValues.maxTeamPlayerPlacings;
                    }
                    if (sanitizedValues.numFactionPlacings === "") {
                      delete sanitizedValues.numFactionPlacings;
                    }

                    if (sanitizedValues.itc) {
                      if (sanitizedValues.maxRTT === "") {
                        delete sanitizedValues.maxRTT;
                      }
                      if (sanitizedValues.maxGT === "") {
                        delete sanitizedValues.maxGT;
                      }
                      if (sanitizedValues.GTEventSize === "") {
                        delete sanitizedValues.GTEventSize;
                      }
                      if (sanitizedValues.MajorEventSize === "") {
                        delete sanitizedValues.MajorEventSize;
                      }
                      if (sanitizedValues.SuperMajorEventSize === "") {
                        delete sanitizedValues.SuperMajorEventSize;
                      }
                    }

                    var apiData = await postData(`/leagues`, {
                      body: sanitizedValues,
                    });
                    window.location.assign(`/organize/circuit/${apiData.id}`);
                    return;
                  }
                } catch (err) {
                  postNotification("error", err);
                } finally {
                  setProcessing(false);
                }

                return;
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                values,
                setFieldValue,
              }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid container sx={{ gap: 2 }}>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 2,
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <ImageUploader
                            values={values}
                            label={values.name}
                            name="circuitImage"
                            setFieldValue={setFieldValue}
                            defaultUrl={
                              circuit?.photo?.url || circuit?.imageUrl
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="name"
                            label="Circuit Name"
                            value={values.name}
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.name && errors.name)}
                            helperText={touched.name && errors.name}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          {circuit?.gameSystemId ? (
                            <TextField
                              label={LABEL_GAMESYSTEM}
                              value={circuit.gameSystem?.name}
                              variant="outlined"
                              fullWidth
                              readOnly
                              margin="normal"
                            />
                          ) : (
                            <GameSystemPicker
                              name="gameType"
                              value={values.gameType || ""}
                              handleChange={handleChange}
                              gameSystems={gameSystems}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          <DatePickerComponent
                            name="startDate"
                            label="Start Date"
                            value={values.startDate}
                            setFieldValue={setFieldValue}
                            error={Boolean(
                              touched.startDate && values.startDate === ""
                            )}
                            helperText={
                              touched.startDate && values.startDate === ""
                                ? "Required"
                                : null
                            }
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <DatePickerComponent
                            name="endDate"
                            label="End Date"
                            value={values.endDate}
                            setFieldValue={setFieldValue}
                            error={Boolean(
                              touched.endDate && values.endDate === ""
                            )}
                            helperText={
                              touched.endDate && values.endDate === ""
                                ? "Required"
                                : null
                            }
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            label="Active"
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)",
                              },
                            }}
                            control={
                              <Switch
                                checked={values.active}
                                onChange={(e) => {
                                  setFieldValue("active", !values.active);
                                }}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>

                        {authContext?.authState?.identity?.isAdmin === true && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              label="Public Circuit"
                              sx={{
                                ml: 0,
                                width: "100%",
                                "& .MuiFormControlLabel-label": {
                                  width: "calc(100% - 58px)",
                                },
                              }}
                              control={
                                <Switch
                                  checked={values.public}
                                  onChange={(e) => {
                                    setFieldValue("public", !values.public);
                                  }}
                                />
                              }
                              labelPlacement="start"
                            />
                          </Grid>
                        )}

                        {(authContext?.authState?.identity?.isAdmin === true ||
                          authContext?.authState?.identity?.adminGameSystems?.includes(
                            circuit?.gameSystemId
                          )) && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              label="Game System Default"
                              sx={{
                                ml: 0,
                                width: "100%",
                                "& .MuiFormControlLabel-label": {
                                  width: "calc(100% - 58px)",
                                },
                              }}
                              control={
                                <Switch
                                  checked={values.bcpDefault}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "bcpDefault",
                                      !values.bcpDefault
                                    );
                                  }}
                                />
                              }
                              labelPlacement="start"
                            />
                          </Grid>
                        )}

                        {SUPPORTS_LOYALTY_STORE &&
                          authContext?.authState?.identity?.isAdmin ===
                            true && (
                            <Grid item xs={12}>
                              <FormControlLabel
                                label="Loyalty Point Circuit"
                                sx={{
                                  ml: 0,
                                  width: "100%",
                                  "& .MuiFormControlLabel-label": {
                                    width: "calc(100% - 58px)",
                                  },
                                }}
                                control={
                                  <Switch
                                    checked={values.loyaltyLeague}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "loyaltyLeague",
                                        !values.loyaltyLeague
                                      );
                                    }}
                                  />
                                }
                                labelPlacement="start"
                              />
                            </Grid>
                          )}

                        <Grid item xs={12}>
                          <FormControlLabel
                            label="Closed"
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)",
                              },
                            }}
                            control={
                              <Switch
                                checked={values.closed}
                                onChange={(e) => {
                                  setFieldValue("closed", !values.closed);
                                }}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            label="Hide Rankings"
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)",
                              },
                            }}
                            control={
                              <Switch
                                checked={values.hidePlacings}
                                onChange={(e) => {
                                  setFieldValue(
                                    "hidePlacings",
                                    !values.hidePlacings
                                  );
                                }}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>

                        {gameStore?.circuitAccess && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              label="Token Circuit"
                              sx={{
                                ml: 0,
                                width: "100%",
                                "& .MuiFormControlLabel-label": {
                                  width: "calc(100% - 58px)",
                                },
                              }}
                              control={
                                <Switch
                                  checked={values.circuitLeague}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "circuitLeague",
                                      !values.circuitLeague
                                    );
                                  }}
                                />
                              }
                              disabled={circuit?.id}
                              labelPlacement="start"
                            />
                          </Grid>
                        )}

                        {values.storeLeague && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              label="Default Organization Circuit"
                              sx={{
                                ml: 0,
                                width: "100%",
                                "& .MuiFormControlLabel-label": {
                                  width: "calc(100% - 58px)",
                                },
                              }}
                              control={
                                <Switch
                                  checked={values.defaultRankings}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "defaultRankings",
                                      !values.defaultRankings
                                    );
                                  }}
                                />
                              }
                              labelPlacement="start"
                            />
                          </Grid>
                        )}

                        {SUPPORTS_HOBBY_TRACK && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              label="Hobby Track Circuit"
                              sx={{
                                ml: 0,
                                width: "100%",
                                "& .MuiFormControlLabel-label": {
                                  width: "calc(100% - 58px)",
                                },
                              }}
                              control={
                                <Switch
                                  checked={values.hobby}
                                  onChange={(e) => {
                                    setFieldValue("hobby", !values.hobby);
                                  }}
                                />
                              }
                              disabled={circuit?.id}
                              labelPlacement="start"
                            />
                          </Grid>
                        )}

                        {values.tokenTypes?.length > 0 ||
                        authContext?.authState?.identity?.isAdmin === true ? (
                          <FieldArray
                            name="tokenTypes"
                            render={(arrayHelpers) => (
                              <>
                                {values.tokenTypes?.length > 0 &&
                                  values.tokenTypes.map((tokenType, index) => {
                                    return (
                                      <Grid key={index} item xs={12}>
                                        <TextField
                                          name={`tokenTypes[${index}]`}
                                          label={`Token Type ${index + 1}`}
                                          value={values.tokenTypes?.[index]}
                                          variant="outlined"
                                          fullWidth
                                          margin="dense"
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          error={Boolean(
                                            touched.tokenTypes?.[index] &&
                                              errors.tokenTypes?.[index]
                                          )}
                                          helperText={
                                            touched.tokenTypes?.[index] &&
                                            errors.tokenTypes?.[index]
                                          }
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <Button
                                                  sx={{ minWidth: "40px" }}
                                                  onClick={() => {
                                                    arrayHelpers.remove(index);
                                                  }}
                                                >
                                                  <BCPTrash />
                                                </Button>
                                              </InputAdornment>
                                            ),
                                          }}
                                        />
                                      </Grid>
                                    );
                                  })}
                                <Grid item xs={12}>
                                  <Button
                                    type="button"
                                    color="inherit"
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<BCPSquarePlus />}
                                    onClick={() => {
                                      arrayHelpers.push("");
                                    }}
                                    sx={{ mb: 2 }}
                                  >
                                    Add Token Type
                                  </Button>
                                </Grid>
                              </>
                            )}
                          />
                        ) : (
                          <></>
                        )}

                        <Grid item xs={12}>
                          <GenericPicker
                            label="Rankings Algorithm"
                            required
                            inclAny={false}
                            name="rankingsAlgorithm"
                            list={rankingsAlgorithms}
                            value={values.rankingsAlgorithm}
                            handleChange={handleChange}
                            disabled={
                              circuit?.id &&
                              authContext?.authState?.identity?.isAdmin !== true
                            }
                          />
                        </Grid>

                        {COMPANY_FLAG === "bcp" && (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                name="numPlayerPlacings"
                                label="Maximum Player Placings"
                                value={values.numPlayerPlacings}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                type="number"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.numPlayerPlacings &&
                                    errors.numPlayerPlacings
                                )}
                                helperText={
                                  touched.numPlayerPlacings &&
                                  errors.numPlayerPlacings
                                }
                              />
                              <FormHelperText>
                                The top X number of placings per Player to be
                                included in a Player's total points.
                              </FormHelperText>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="numTeamPlacings"
                                label="Maximum Team Placings"
                                value={values.numTeamPlacings}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                type="number"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.numTeamPlacings &&
                                    errors.numTeamPlacings
                                )}
                                helperText={
                                  touched.numTeamPlacings &&
                                  errors.numTeamPlacings
                                }
                              />
                              <FormHelperText>
                                The top X number of placings per Team to be
                                included in a Team's total points.
                              </FormHelperText>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="maxTeamPlayerPlacings"
                                label="Maximum Player Placings per Team Placings"
                                value={values.maxTeamPlayerPlacings}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                type="number"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.maxTeamPlayerPlacings &&
                                    errors.maxTeamPlayerPlacings
                                )}
                                helperText={
                                  touched.maxTeamPlayerPlacings &&
                                  errors.maxTeamPlayerPlacings
                                }
                              />
                              <FormHelperText>
                                The total number of placings per Player per Team
                                to be included in a Team's total points.
                              </FormHelperText>
                              <FormHelperText>
                                Eg: If the Maximum Team Placings is set as 10,
                                and this value is set to 3, then a single player
                                cannot account for more than 3 of the top 10
                                placings for a team, their subsequent placings
                                are ignored.
                              </FormHelperText>
                            </Grid>

                            {(circuit?.gameSystem?.hasFactions ||
                              (!circuit &&
                                gameSystemDict[values.gameType]
                                  ?.hasFactions)) && (
                              <Grid item xs={12}>
                                <TextField
                                  name="numFactionPlacings"
                                  label={`Maximum number of Placings per Player Per ${
                                    circuit?.gameSystem?.factionLabel ||
                                    gameSystemDict[values.gameType]
                                      ?.factionLabel ||
                                    "Faction"
                                  }`}
                                  value={values.numFactionPlacings}
                                  variant="outlined"
                                  fullWidth
                                  margin="normal"
                                  type="number"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={Boolean(
                                    touched.numFactionPlacings &&
                                      errors.numFactionPlacings
                                  )}
                                  helperText={
                                    touched.numFactionPlacings &&
                                    errors.numFactionPlacings
                                  }
                                />
                                <FormHelperText>
                                  The top X number of placings per Player per{" "}
                                  {circuit?.gameSystem?.factionLabel ||
                                    gameSystemDict[values.gameType]
                                      ?.factionLabel ||
                                    "Faction"}{" "}
                                  to be included in a player's{" "}
                                  {circuit?.gameSystem?.factionLabel ||
                                    gameSystemDict[values.gameType]
                                      ?.factionLabel ||
                                    "Faction"}{" "}
                                  ranking points.
                                </FormHelperText>
                              </Grid>
                            )}
                          </>
                        )}

                        {values.rankingsAlgorithm === "ITC Points" && (
                          <Grid item xs={12}>
                            <Accordion>
                              <AccordionSummary
                                expandIcon={<BCPAngleDown />}
                                aria-controls="itc-content"
                                id="itc-header"
                              >
                                <Typography>ITC Settings</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <TextField
                                      name="maxRTT"
                                      label="Maximum number of Small Events in Rankings"
                                      value={values.maxRTT}
                                      variant="outlined"
                                      fullWidth
                                      margin="normal"
                                      type="number"
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      error={Boolean(
                                        touched.maxRTT && errors.maxRTT
                                      )}
                                      helperText={
                                        touched.maxRTT && errors.maxRTT
                                      }
                                    />
                                    <Typography variant="caption">
                                      Leave blank if no maximum
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      name="GTEventSize"
                                      label="Grand Tournament (GT) Event Size"
                                      value={values.GTEventSize}
                                      variant="outlined"
                                      fullWidth
                                      margin="normal"
                                      type="number"
                                      required
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      error={Boolean(
                                        touched.GTEventSize &&
                                          errors.GTEventSize
                                      )}
                                      helperText={
                                        touched.GTEventSize &&
                                        errors.GTEventSize
                                      }
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      name="maxGT"
                                      label="Maximum number of GT Placings in Rankings"
                                      value={values.maxGT}
                                      variant="outlined"
                                      fullWidth
                                      margin="normal"
                                      type="number"
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      error={Boolean(
                                        touched.maxGT && errors.maxGT
                                      )}
                                      helperText={touched.maxGT && errors.maxGT}
                                    />
                                    <Typography variant="caption">
                                      Leave blank if no maximum
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      name="MajorEventSize"
                                      label="Major Event Size"
                                      value={values.MajorEventSize}
                                      variant="outlined"
                                      fullWidth
                                      margin="normal"
                                      type="number"
                                      required
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      error={Boolean(
                                        touched.MajorEventSize &&
                                          errors.MajorEventSize
                                      )}
                                      helperText={
                                        touched.MajorEventSize &&
                                        errors.MajorEventSize
                                      }
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      name="SuperMajorEventSize"
                                      label="Super Major Event Size"
                                      value={values.SuperMajorEventSize}
                                      variant="outlined"
                                      fullWidth
                                      margin="normal"
                                      type="number"
                                      required
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      error={Boolean(
                                        touched.SuperMajorEventSize &&
                                          errors.SuperMajorEventSize
                                      )}
                                      helperText={
                                        touched.SuperMajorEventSize &&
                                        errors.SuperMajorEventSize
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <Accordion>
                            <AccordionSummary
                              expandIcon={<BCPAngleDown />}
                              aria-controls="elo-content"
                              id="elo-header"
                            >
                              <Typography>
                                ELO Settings
                                <HelpButton title="Help document coming soon..." />
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.defaultELO"
                                    label="Starting Rating"
                                    value={values.ELOOptions?.defaultELO}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.defaultELO &&
                                        errors.ELOOptions?.defaultELO
                                    )}
                                    helperText={
                                      touched.ELOOptions?.defaultELO &&
                                      errors.ELOOptions?.defaultELO
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.minimumELO"
                                    label="Minimum Rating"
                                    value={values.ELOOptions?.minimumELO}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.minimumELO &&
                                        errors.ELOOptions?.minimumELO
                                    )}
                                    helperText={
                                      touched.ELOOptions?.minimumELO &&
                                      errors.ELOOptions?.minimumELO
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.maxRatingDifference"
                                    label="Maximum Rating Difference"
                                    value={
                                      values.ELOOptions?.maxRatingDifference
                                    }
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.maxRatingDifference &&
                                        errors.ELOOptions?.maxRatingDifference
                                    )}
                                    helperText={
                                      touched.ELOOptions?.maxRatingDifference &&
                                      errors.ELOOptions?.maxRatingDifference
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.k.numerator"
                                    label="K Factor Numerator"
                                    value={values.ELOOptions?.k?.numerator}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.k?.numerator &&
                                        errors.ELOOptions?.k?.numerator
                                    )}
                                    helperText={
                                      touched.ELOOptions?.k?.numerator &&
                                      errors.ELOOptions?.k?.numerator
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: "600" }}
                                  >
                                    Effective Number of Games Played
                                  </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.ne.maxN"
                                    label="Maximum"
                                    value={values.ELOOptions?.ne?.maxN}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.ne?.maxN &&
                                        errors.ELOOptions?.ne?.maxN
                                    )}
                                    helperText={
                                      touched.ELOOptions?.ne?.maxN &&
                                      errors.ELOOptions?.ne?.maxN
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.ne.level1"
                                    label="Level 1"
                                    value={values.ELOOptions?.ne?.level1}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.ne?.level1 &&
                                        errors.ELOOptions?.ne?.level1
                                    )}
                                    helperText={
                                      touched.ELOOptions?.ne?.level1 &&
                                      errors.ELOOptions?.ne?.level1
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.ne.level2"
                                    label="Level 2"
                                    value={values.ELOOptions?.ne?.level2}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.ne?.level2 &&
                                        errors.ELOOptions?.ne?.level2
                                    )}
                                    helperText={
                                      touched.ELOOptions?.ne?.level2 &&
                                      errors.ELOOptions?.ne?.level2
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.ne.a"
                                    label="a"
                                    value={values.ELOOptions?.ne?.a}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.ne?.a &&
                                        errors.ELOOptions?.ne?.a
                                    )}
                                    helperText={
                                      touched.ELOOptions?.ne
                                        ?.apiGameSystemDict &&
                                      errors.ELOOptions?.ne?.a
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.ne.b"
                                    label="b"
                                    value={values.ELOOptions?.ne?.b}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.ne?.b &&
                                        errors.ELOOptions?.ne?.b
                                    )}
                                    helperText={
                                      touched.ELOOptions?.ne?.b &&
                                      errors.ELOOptions?.ne?.b
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: "600" }}
                                  >
                                    ELO Bonus
                                  </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.t"
                                    label="T"
                                    value={values.ELOOptions?.t}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.t &&
                                        errors.ELOOptions?.t
                                    )}
                                    helperText={
                                      touched.ELOOptions?.t &&
                                      errors.ELOOptions?.t
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="ELOOptions.b.minGamesPlayed"
                                    label="B"
                                    value={values.ELOOptions?.b?.minGamesPlayed}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                    margin="normal"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.ELOOptions?.b?.minGamesPlayed &&
                                        errors.ELOOptions?.b?.minGamesPlayed
                                    )}
                                    helperText={
                                      touched.ELOOptions?.b?.minGamesPlayed &&
                                      errors.ELOOptions?.b?.minGamesPlayed
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: "600" }}
                                  >
                                    ELO Classes
                                  </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                  <FormControlLabel
                                    label="Floor ELO Classes"
                                    sx={{
                                      ml: 0,
                                      width: "100%",
                                      "& .MuiFormControlLabel-label": {
                                        width: "calc(100% - 58px)",
                                      },
                                    }}
                                    control={
                                      <Switch
                                        checked={
                                          values.ELOOptions?.floorELOClass
                                        }
                                        onChange={(e) => {
                                          setFieldValue(
                                            "ELOOptions.floorELOClass",
                                            !values.ELOOptions?.floorELOClass
                                          );
                                        }}
                                      />
                                    }
                                    labelPlacement="start"
                                  />
                                </Grid>

                                <FieldArray
                                  name="ELOOptions.ELOClasses"
                                  render={(arrayHelpers) => (
                                    <>
                                      {values.ELOOptions.ELOClasses?.length >
                                        0 &&
                                        values.ELOOptions.ELOClasses.map(
                                          (eloClass, index) => {
                                            return (
                                              <Grid
                                                key={index}
                                                item
                                                xs={12}
                                                sx={{ p: 1 }}
                                              >
                                                <Grid
                                                  container
                                                  spacing={1}
                                                  sx={{
                                                    border:
                                                      "1px solid rgba(0, 0, 0, 0.12)",
                                                    borderRadius: 1,
                                                    boxShadow: 2,
                                                    p: 1,
                                                  }}
                                                >
                                                  <Grid item xs={12}>
                                                    <TextField
                                                      name={`ELOOptions.ELOClasses[${index}].label`}
                                                      label="Name"
                                                      value={
                                                        values.ELOOptions
                                                          .ELOClasses[index]
                                                          .label
                                                      }
                                                      variant="outlined"
                                                      fullWidth
                                                      required
                                                      margin="normal"
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      error={Boolean(
                                                        touched.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.label &&
                                                          errors.ELOOptions
                                                            ?.ELOClasses?.[
                                                            index
                                                          ]?.label
                                                      )}
                                                      helperText={
                                                        touched.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.label &&
                                                        errors.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.label
                                                      }
                                                    />
                                                  </Grid>
                                                  <Grid item xs={12}>
                                                    <TextField
                                                      name={`ELOOptions.ELOClasses[${index}].min`}
                                                      label="Minimum Points"
                                                      value={
                                                        values.ELOOptions
                                                          .ELOClasses[index].min
                                                      }
                                                      variant="outlined"
                                                      fullWidth
                                                      type="number"
                                                      inputProps={{
                                                        min: 0,
                                                      }}
                                                      required
                                                      margin="normal"
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      error={Boolean(
                                                        touched.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.min &&
                                                          errors.ELOOptions
                                                            ?.ELOClasses?.[
                                                            index
                                                          ]?.min
                                                      )}
                                                      helperText={
                                                        touched.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.min &&
                                                        errors.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.min
                                                      }
                                                    />
                                                  </Grid>
                                                  <Grid item xs={12}>
                                                    <TextField
                                                      name={`ELOOptions.ELOClasses[${index}].max`}
                                                      label="Maximum Points"
                                                      value={
                                                        values.ELOOptions
                                                          .ELOClasses[index].max
                                                      }
                                                      variant="outlined"
                                                      fullWidth
                                                      type="number"
                                                      inputProps={{
                                                        min: 0,
                                                      }}
                                                      margin="normal"
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      error={Boolean(
                                                        touched.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.max &&
                                                          errors.ELOOptions
                                                            ?.ELOClasses?.[
                                                            index
                                                          ]?.max
                                                      )}
                                                      helperText={
                                                        touched.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.max &&
                                                        errors.ELOOptions
                                                          ?.ELOClasses?.[index]
                                                          ?.max
                                                      }
                                                    />
                                                  </Grid>
                                                  <Grid item xs={12}>
                                                    <Button
                                                      type="button"
                                                      color="inherit"
                                                      variant="outlined"
                                                      fullWidth
                                                      startIcon={<BCPTrash />}
                                                      onClick={() => {
                                                        arrayHelpers.remove(
                                                          index
                                                        );
                                                      }}
                                                    >
                                                      Remove Class
                                                    </Button>
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            );
                                          }
                                        )}
                                      <Grid item xs={12}>
                                        <Button
                                          type="button"
                                          color="inherit"
                                          variant="outlined"
                                          fullWidth
                                          startIcon={<BCPSquarePlus />}
                                          onClick={() => {
                                            arrayHelpers.push({
                                              label: "",
                                              min:
                                                (values.ELOOptions.ELOClasses
                                                  .length
                                                  ? values.ELOOptions
                                                      .ELOClasses[
                                                      values.ELOOptions
                                                        .ELOClasses.length - 1
                                                    ].max || 0
                                                  : 0) + 1,
                                              max:
                                                (values.ELOOptions.ELOClasses
                                                  .length
                                                  ? values.ELOOptions
                                                      .ELOClasses[
                                                      values.ELOOptions
                                                        .ELOClasses.length - 1
                                                    ].max || 0
                                                  : 0) + 100,
                                            });
                                          }}
                                        >
                                          Add Class
                                        </Button>
                                      </Grid>
                                    </>
                                  )}
                                />
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>

                        <Grid item xs={12}>
                          <GenericPicker
                            label="Default Placing Type"
                            required
                            inclAny={false}
                            name="defaultPlacingType"
                            list={["Ranking Points", "ELO Points"]}
                            value={values.defaultPlacingType}
                            handleChange={handleChange}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            ref={saveBoxRef}
                            fullWidth
                            color="success"
                            variant="contained"
                            type="submit"
                          >
                            Save
                          </Button>
                        </Grid>

                        {!saveBoxInView && (
                          <Box
                            sx={{
                              position: "fixed",
                              zIndex: 999,
                              p: 2,
                              bgcolor: "background.paper",
                              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                              boxShadow: 3,
                              bottom: 0,
                              left: [0, 0, 0, 256],
                              width: [
                                "100%",
                                "100%",
                                "100%",
                                "calc(100% - 256px)",
                              ],
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Button
                                fullWidth
                                color="success"
                                variant="contained"
                                type="submit"
                              >
                                Save
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          )}
        </Grid>
      </Grid>

      {Boolean(loading || processing) && <ModalLoadingFull />}
    </Container>
  );
}
